import { useState } from 'react'
import type { Concert } from '../domain/program-schema.ts'
import { imageProxyUrl } from '../preload.ts'
import './ConcertImage.css'

/**
 * Blur-up artist image.
 *
 * The snapshot carries a base64 placeholder (`lqip`) alongside the CDN url, so the
 * space is filled with a blurred approximation of the real photo on the first frame
 * — no layout shift and no grey box, which is what a festival network would
 * otherwise give you for several seconds.
 *
 * The url comes from `src/preload.ts`, and the direction of that import is deliberate:
 * the warm-up and the service worker route are in the same module, and a Cache Storage
 * entry is keyed by the exact url, so all three have to be one function. It is also why
 * there is no `srcSet` any more — a responsive set means the browser picks a url the
 * warm-up cannot know in advance, and at `w=900` the widest candidate only bought a
 * 1.3× upscale on DPR-3 phones anyway (KTD11). No `crossorigin` either: the image is
 * same-origin now, and the CDN refuses any request that carries an `Origin` header.
 */
export function ConcertImage({ concert }: { concert: Concert }) {
  const [loaded, setLoaded] = useState(false)

  const src = imageProxyUrl(concert.image)
  if (!src) {
    return <div className="concert-image concert-image--empty" aria-hidden="true" />
  }

  return (
    <div
      className="concert-image"
      style={concert.lqip ? { backgroundImage: `url(${concert.lqip})` } : undefined}
    >
      <img
        src={src}
        alt={concert.name}
        loading="eager"
        decoding="async"
        data-loaded={loaded || undefined}
        onLoad={() => setLoaded(true)}
      />
    </div>
  )
}
